import ContentCardSmall from '@/features/CardCarousel/variants/ContentCardCarousel/ContentCardSmall';
import ContentContainer from '@/features/shared/ContentContainer';
import DetailContent from '@/features/shared/DetailContent';
import { datalayerPush } from '@/features/shared/HOCs/withGTM';
import {
  MuiButtonBase,
  MuiContainer,
  MuiTab,
  MuiTabs,
  MuiTypography,
} from '@/features/shared/material-ui';
import RichText from '@/features/shared/RichText';
import SwiperCarousel from '@/features/shared/SwiperCarousel';
import { SpacingSizes, Theme } from '@/utils/constants';
import { mapLinkToProps } from '@/utils/helpers';
import { resolveTheme } from '@/utils/resolvers';
import { isUserInteraction } from '@/utils/swiper.helpers';
import { useMediaQuery } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import clsx from 'clsx';
import { isUndefined } from 'lodash';
import React, { ChangeEvent, forwardRef, memo, useEffect, useMemo, useRef, useState } from 'react';
import TagManager from 'react-gtm-module';
import VisibilitySensor from 'react-visibility-sensor';
import Swiper from 'swiper';
import { SwiperSlide } from 'swiper/react';
import { VerticalWithCarouselProps } from './VerticalWithCarousel.interface';
import useStyles from './VerticalWithCarousel.styles';

const a11yProps = (index: any, isMobile: boolean) => {
  return {
    id: `vertical-with-carousel-tab-${index}`,
    'aria-controls': [
      `vertical-with-carousel-tabpanel-${index}`,
      isMobile ? `vertical-with-carousel-mobiletabpanel-${index}` : '',
    ]
      .join(' ')
      .trim(),
  };
};

interface TabPanelProps {
  children?: any;
  index?: any;
  value?: any;
  panelName?: string;
  classes?: any;
}

const mapVariantToDetailContentClassName =
  (classes: any) =>
  ({ variant }: any) => {
    if (/^rtc$/.test(variant)) {
      return classes.rtc;
    }

    if (/^link$/.test(variant)) {
      return classes.link;
    }

    return '';
  };

const TabPanel = memo((props: TabPanelProps) => {
  const { children, value, index, panelName, classes, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`vertical-with-carousel-${panelName}-${index}`}
      aria-labelledby={`vertical-with-carousel-tab-${index}`}
      className={classes.tabPanel}
      {...other}
    >
      {children}
    </div>
  );
});

const TabPanelCarousel = memo(({ cards, theme, tabTheme, tabColor, classes, tabIndex }: any) => {
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));
  const [slider, setSlider] = useState<any>(null);
  const [slideIndex, setSlideIndex] = React.useState<number>();
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [triggerCarouselNavClick, setTriggerCarouselNavClick] = React.useState(false);

  useEffect(() => {
    if (isUndefined(slideIndex)) {
      return;
    }

    const variantName = (
      containerRef?.current?.closest('[data-datalayer-variant]') as HTMLDivElement
    )?.dataset?.datalayerVariant;
    datalayerPush({
      compRef: containerRef,
      args: {
        tabTitle: cards[slideIndex]?.cardHeading || '',
        variantName,
      },
      event: 'gtm:custom:scroll',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slideIndex]);

  React.useEffect(() => {
    if (!triggerCarouselNavClick) return;
    if (!slideIndex) return;

    const dataLayer = {
      event: 'trackEvent-cardCarouselNavClick',
      event_name: 'card_carousel_nav_click',
      title: cards?.[slideIndex]?.cardHeading,
      index_num: String(slideIndex),
    };
    TagManager.dataLayer({ dataLayer });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [triggerCarouselNavClick]);

  const configuration = useRef({
    autoplay: false,
    spaceBetween: isMobile ? 32 : 20,
    slidesPerView: 'auto',
    centerInsufficientSlides: isMobile,
    centeredSlides: isMobile,
    loop: isMobile,
    observer: true,
    loopedSlides: isMobile ? cards.length : undefined,
    cssMode: true,
    pagination: {
      clickable: true,
      el: `.${classes.sliderNavigation}`,
    },
    onSwiper: (swiper: any) => setSlider(swiper),
    onSlideChange: (swiperInstance: Swiper) => {
      const { realIndex } = swiperInstance;

      if (isUserInteraction(swiperInstance)) {
        setTriggerCarouselNavClick(false);
        setSlideIndex(realIndex);

        Promise.resolve().then(() => {
          setTriggerCarouselNavClick(() => true);
        });
      }
    },
  });

  useEffect(() => {
    if (slider && !slider.destroyed) {
      Promise.resolve().then(() => {
        slider.update();

        if (!isMobile && slider.realIndex !== 0) {
          slider.slideTo(0);
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cards, slider]);

  const allCards = useMemo(
    () =>
      cards.map((card: CardType, index: number) => {
        const linkProps = mapLinkToProps({ href: card.url });

        return (
          <SwiperSlide
            className={classes.sliderCard}
            key={card.cardHeading}
            data-datalayer-card-index={index}
            data-datalayer-card-title={card.cardHeading}
          >
            <MuiButtonBase {...linkProps} className={classes.sliderCard}>
              <ContentCardSmall data={[card]} theme={tabTheme} cardColor={tabColor} elevation={0} />
            </MuiButtonBase>
          </SwiperSlide>
        );
      }),
    [cards, classes.sliderCard, tabColor, tabTheme],
  );

  return (
    <SwiperCarousel
      id={`carousel-tab-${tabIndex}`}
      ref={containerRef}
      sliderNavigationClass="desktop-tab-pagination"
      navigation
      slider={slider}
      classes={classes}
      theme={tabTheme}
      mdTheme={theme}
      {...configuration.current}
    >
      {allCards}
    </SwiperCarousel>
  );
});

const DetailContentPanels = memo(
  ({
    classes,
    tabs = [],
    value = 0,
    theme = Theme.light,
    tabTheme = Theme.dark,
    tabColor,
    hasTextShadow,
  }: {
    classes?: any;
    tabs?: TabContentType[];
    value?: number;
    theme?: ThemeType;
    tabTheme?: ThemeType;
    tabColor?: string;
    hasTextShadow?: boolean;
  }) => {
    const { tabDetailContent: activeTabDetailContent } = useMemo(
      () => tabs[value] || {},
      [tabs, value],
    );
    const { detailContent: activeDetailContent = [] } = activeTabDetailContent || {};
    const cardList = useMemo(
      () => activeDetailContent.filter((content) => /card/.test(content.variant)),
      [activeDetailContent],
    );
    const tabPanelCarouselContentClassName = clsx(
      classes.tabPanelCarouselContent,
      hasTextShadow && 'has-text-shadow',
    );
    const tabPanelDetailContentClassName = clsx(
      classes.tabPanelDetailContent,
      `bg-color-${tabColor}-md`,
    );
    const rootClasses = clsx(classes.tabPanels, `${tabTheme}-theme`);
    return (
      <div className={rootClasses}>
        <div className={tabPanelDetailContentClassName}>
          {tabs.map(({ tabDetailContent = {} }, i) => {
            const { detailContent = [], heading, superHeading } = tabDetailContent;
            const nonCardList = detailContent.filter((content) => !/card/.test(content.variant));
            const span = document.createElement('span');
            span.innerHTML = heading || '';
            const headingParsed = span.textContent || span.innerText;

            return (
              <TabPanel
                // eslint-disable-next-line react/no-array-index-key
                key={`tabPanel_${i}`}
                value={value}
                panelName="tabpanel"
                index={i}
                classes={classes}
                data-datalayer-card-index={i}
                data-datalayer-card-title={superHeading || headingParsed}
              >
                <div className={classes.tabPanelAnimated}>
                  {superHeading && (
                    <MuiTypography
                      variant="overline"
                      component="h5"
                      className={classes.superheading}
                    >
                      {superHeading}
                    </MuiTypography>
                  )}
                  {heading && (
                    <RichText
                      customStyles
                      theme={tabTheme}
                      className={clsx(classes.heading, 'gtm-headline')}
                      content={heading}
                    />
                  )}
                  <DetailContent
                    content={nonCardList}
                    theme={tabTheme}
                    mapVariantToClassName={mapVariantToDetailContentClassName(classes)}
                  />
                </div>
              </TabPanel>
            );
          })}
        </div>
        <div className={tabPanelCarouselContentClassName}>
          {cardList.length > 0 && (
            <TabPanelCarousel
              tabIndex={value}
              cards={cardList}
              theme={theme}
              tabTheme={tabTheme}
              tabColor={tabColor}
              classes={classes}
            />
          )}
        </div>
      </div>
    );
  },
);

const CustomTabComponent = forwardRef((props: any, ref: any) => {
  const { children, ...tabProps } = props;
  return (
    <div {...tabProps} ref={ref}>
      <MuiTypography className="heading" variant="body1" component="h3">
        {children}
      </MuiTypography>
    </div>
  );
});

const VerticalWithCarousel = ({
  theme = Theme.dark,
  tabGroupTitle,
  tabs = [],
  tabColor,
  containerProps,
  contentButtons,
  hasTextShadow,
}: Br.PropsWithContentButtons<VerticalWithCarouselProps>) => {
  const classes = useStyles({});
  const [value, setValue] = useState(0);
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));
  const [sensorIndex, setSensorIndex] = useState([0]);
  const backgroundImages = useMemo(
    () => tabs.map((tab) => tab?.tabContainerBackgroundImage),
    [tabs],
  );
  const tabTheme = resolveTheme('bg-color')({ rawVal: tabColor });
  const hasBackgroundImage = useMemo(
    () =>
      (containerProps?.backgroundImage ||
        (backgroundImages.length > 0 && backgroundImages.some((img) => img !== undefined))) &&
      !isMobile,
    [backgroundImages, containerProps?.backgroundImage, isMobile],
  );
  const [isOversized, setOversized] = React.useState(false);
  const rootRef = React.useRef(document.createElement('div'));
  const contentRef = React.useRef(document.createElement('div'));

  // eslint-disable-next-line @typescript-eslint/ban-types
  const handleChange = (event: ChangeEvent<{}>, newValue: number) => {
    setValue(newValue);
    const dataLayer = {
      event: 'trackEvent-scrollCarouselNavClick',
      event_name: 'scroll_carousel_nav_click',
      title: tabs[newValue]?.tabTitle?.text,
      index_num: String(newValue),
    };
    TagManager.dataLayer({ dataLayer });
  };

  const handleVisibilityChange = (isVisible: any, index: any) => {
    if (isVisible && !isOversized) {
      const validateIfWasScrolled = sensorIndex.some((x: any) => x === index);
      if (!validateIfWasScrolled) {
        setSensorIndex((prev) => [...prev, index]);
        const dataLayer = {
          event: 'trackEvent-scrollCarouselNavScroll',
          event_name: 'scroll_carousel_nav_scroll',
          title: tabs[index]?.tabTitle?.text,
          index_num: String(index),
        };
        TagManager.dataLayer({ dataLayer });
      }
      setValue(index);
    }
  };

  React.useEffect(() => {
    const checkOversized = () => {
      if (!isMobile) {
        const HEADER_HEIGHT = 58;
        const PANEL_MARGIN_TOP = 100;
        const CONTAINER_PADDING = 60;
        const contentHeight =
          [...contentRef.current.children].reduce((a, b: any) => a + b.offsetHeight, 0) +
          PANEL_MARGIN_TOP +
          CONTAINER_PADDING;
        const realVH = document.documentElement.clientHeight - HEADER_HEIGHT;
        if (contentHeight >= realVH && !isOversized) setOversized(true);
        else if (contentHeight < realVH && isOversized) setOversized(false);
      }
    };

    checkOversized();

    if (!isMobile && !isOversized) {
      rootRef.current.classList.add('is-sticky');
    } else {
      rootRef.current.classList.remove('is-sticky');
    }

    window.addEventListener('resize', checkOversized);
    return () => window.removeEventListener('resize', checkOversized);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile, isOversized]);

  const rootClassName = clsx(classes.root, 'is-sticky');
  const rootTabsClassName = clsx(classes.tabsRoot, `${tabTheme}-theme`, `${theme}-theme-md`);
  const tabClassName = clsx(classes.tab, 'gtm-tab');

  return (
    <ContentContainer
      {...containerProps}
      topBackgroundColor={hasBackgroundImage ? 'transparent' : containerProps?.topBackgroundColor}
      bottomBackgroundColor={
        hasBackgroundImage ? 'transparent' : containerProps?.bottomBackgroundColor
      }
      topSpacing={SpacingSizes.none}
      bottomSpacing={SpacingSizes.none}
      mobileTopBackgroundColor={tabColor}
      mobileBottomBackgroundColor={tabColor}
      multipleBackground={backgroundImages.length > 0}
      multipleBackgroundImage={backgroundImages}
      multipleBackgroundSelected={value}
      mobileTopSpacing={SpacingSizes.small}
      mobileBottomSpacing={SpacingSizes.small}
      data-component-variant="vertical-with-carousel"
    >
      <MuiContainer className="largeRight">
        <div className={rootClassName} ref={rootRef}>
          <div className={classes.content} ref={contentRef}>
            <div className={rootTabsClassName}>
              {contentButtons?.content}

              {tabGroupTitle && (
                <MuiTypography variant="overline" component="h3" className={classes.title}>
                  {tabGroupTitle}
                </MuiTypography>
              )}

              {tabs.length > 0 && (
                <MuiTabs
                  orientation={isMobile ? 'horizontal' : 'vertical'}
                  variant={isMobile ? 'scrollable' : 'standard'}
                  scrollButtons={isMobile ? 'on' : 'off'}
                  value={value}
                  onChange={handleChange}
                  aria-label="Vertical tabs"
                  className={classes.tabs}
                >
                  {tabs.map(({ tabTitle }, i) => (
                    <MuiTab
                      // eslint-disable-next-line react/no-array-index-key
                      key={`tab_${i}`}
                      label={tabTitle?.text}
                      {...a11yProps(i, isMobile)}
                      component={CustomTabComponent}
                      className={tabClassName}
                    />
                  ))}
                </MuiTabs>
              )}
            </div>

            <DetailContentPanels
              theme={theme}
              tabs={tabs}
              tabTheme={tabTheme}
              tabColor={tabColor}
              value={value}
              classes={classes}
              hasTextShadow={hasTextShadow}
            />
          </div>
        </div>
        <div>
          {!isMobile &&
            !isOversized &&
            tabs.map((_, index) => (
              <div className={classes.trigger} key={index.toString()}>
                <VisibilitySensor
                  onChange={(isVisible: boolean) => handleVisibilityChange(isVisible, index)}
                  delayedCall
                  scrollCheck
                  partialVisibility
                >
                  <span aria-hidden="true">&nbsp;</span>
                </VisibilitySensor>
              </div>
            ))}
        </div>
      </MuiContainer>
    </ContentContainer>
  );
};

export default memo(VerticalWithCarousel);
